import * as React from 'react';
import CategorySearchSuggestion from './CategorySearchSuggestion';
import FileSearchSuggestion from './FileSearchSuggestion';
import MessageSearchSuggestion from './MessageSearchSuggestion';
import SegmentedSearchSuggestion from './SegmentedSearchSuggestion';
import TextSearchSuggestion from './TextSearchSuggestion';
import EventSearchSuggestion from './EventSearchSuggestion';
import {
    lazyLogLocalContentLayout,
    LocalContentLayout,
    LocalContentLayoutGroup,
    Suggestion,
    SuggestionKind,
    SuggestionSet,
} from 'owa-search-service';
import { PeopleSearchSuggestion } from './PeopleSearchSuggestion';
import { SearchScenarioId } from 'owa-search-next-common';
import getSubstrateSearchScenarioBySearchScenarioId from '../../utils/getSubstrateSearchScenarioBySearchScenarioId';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    suggestionsHeaderNext,
    potentialMatchesHeaderNext,
} from './SearchSuggestion.locstring.json';
import loc from 'owa-localize';

const styles = require('./SearchSuggestion.scss');

export default function getSuggestionCalloutContent(
    suggestionSet: SuggestionSet,
    scenarioId: SearchScenarioId
): JSX.Element[] {
    if (!suggestionSet) {
        return [];
    }

    /**
     * Cache existing items (if new suggestions are still in progress) to be
     * returned if new suggestions aren't ready to be displayed.
     */
    const items: JSX.Element[] = [];
    const localContentLayoutGroups: { [groupType: string]: LocalContentLayout[] } = {};

    const allSuggestions = suggestionSet.Suggestions;
    const traceId = suggestionSet.TraceId;

    const suggestionSuggestions = getSuggestionsSuggestionGroup(allSuggestions);
    const potentialMatchSuggestions = getPotentialMatchesSuggestionGroup(allSuggestions);

    // Suggestions that go under the "Suggestions" header (i.e. suggestions that execute a search)
    if (suggestionSuggestions.length > 0) {
        if (isFeatureEnabled('sea-qf-headers')) {
            items.push(<div className={styles.header}>{loc(suggestionsHeaderNext)}</div>);
        }

        suggestionSuggestions.map((suggestion: Suggestion, index: number) => {
            addSuggestionComponent(
                items,
                suggestion,
                index,
                scenarioId,
                traceId,
                localContentLayoutGroups
            );
        });
    }

    // Add horizontal separator if we have 2 groups
    if (suggestionSuggestions.length > 0 && potentialMatchSuggestions.length > 0) {
        items.push(<hr className={styles.divider} />);
    }

    // Suggestions that go under the "Potential matches" header (i.e. suggestions that are actually search results)
    if (potentialMatchSuggestions.length > 0) {
        if (isFeatureEnabled('sea-qf-headers')) {
            items.push(<div className={styles.header}>{loc(potentialMatchesHeaderNext)}</div>);
        }

        potentialMatchSuggestions.map((suggestion: Suggestion, index: number) => {
            // Need to account for first group of suggestions.
            const adjustedIndex = suggestionSuggestions.length + index;

            addSuggestionComponent(
                items,
                suggestion,
                adjustedIndex,
                scenarioId,
                traceId,
                localContentLayoutGroups
            );
        });
    }

    if (suggestionSet.IsComplete && Object.keys(localContentLayoutGroups).length > 0) {
        lazyLogLocalContentLayout.importAndExecute(
            suggestionSet.TraceId,
            localContentLayoutGroups,
            getSubstrateSearchScenarioBySearchScenarioId(scenarioId)
        );
    }

    return items;
}

const getSuggestionsSuggestionGroup = (suggestions: Suggestion[]): Suggestion[] => {
    return suggestions.filter((suggestion: Suggestion) => {
        const suggestionKind = suggestion.kind;

        switch (suggestionKind) {
            case SuggestionKind.Keywords:
            case SuggestionKind.People:
            case SuggestionKind.PrivateDistributionList:
            case SuggestionKind.Category:
                return true;
            default:
                return false;
        }
    });
};

const getPotentialMatchesSuggestionGroup = (suggestions: Suggestion[]): Suggestion[] => {
    return suggestions.filter((suggestion: Suggestion) => {
        const suggestionKind = suggestion.kind;

        switch (suggestionKind) {
            case SuggestionKind.Files:
            case SuggestionKind.Message:
            case SuggestionKind.Event:
                return true;
            default:
                return false;
        }
    });
};

const addSuggestionComponent = (
    items: JSX.Element[],
    suggestion: Suggestion,
    index: number,
    scenarioId: SearchScenarioId,
    traceId: string,
    localContentLayoutGroups: { [groupType: string]: LocalContentLayout[] }
) => {
    // Subset of SearchSuggestion props that all suggestions share.
    const sharedProps = {
        index: index,
        scenarioId: scenarioId,
        suggestionSetTraceId: traceId,
    };

    // Add the appropriate component based on suggestion kind.
    switch (suggestion.kind) {
        case SuggestionKind.People:
            items.push(<PeopleSearchSuggestion {...sharedProps} suggestion={suggestion} />);

            if (suggestion.Source === 'gal') {
                const galGroupType: LocalContentLayoutGroup = 'gal';
                if (!localContentLayoutGroups[galGroupType]) {
                    localContentLayoutGroups[galGroupType] = [];
                }

                // add to local content layout
                localContentLayoutGroups[galGroupType].push({
                    entityId: suggestion.ReferenceId,
                    position: index + 1,
                });
            }
            break;
        case SuggestionKind.Keywords:
            if (isFeatureEnabled('sea-gf') && suggestion.Segments) {
                items.push(<SegmentedSearchSuggestion {...sharedProps} suggestion={suggestion} />);
            } else {
                items.push(<TextSearchSuggestion {...sharedProps} suggestion={suggestion} />);
            }
            break;
        case SuggestionKind.Message:
            items.push(<MessageSearchSuggestion {...sharedProps} suggestion={suggestion} />);
            break;
        case SuggestionKind.Category:
            items.push(<CategorySearchSuggestion {...sharedProps} suggestion={suggestion} />);

            const categoryGroupType: LocalContentLayoutGroup = 'category';
            if (!localContentLayoutGroups[categoryGroupType]) {
                localContentLayoutGroups[categoryGroupType] = [];
            }

            // add to local content layout
            localContentLayoutGroups[categoryGroupType].push({
                entityId: suggestion.ReferenceId,
                position: index + 1,
            });
            break;
        case SuggestionKind.Files:
            items.push(<FileSearchSuggestion {...sharedProps} suggestion={suggestion} />);
            break;
        case SuggestionKind.Event:
            items.push(<EventSearchSuggestion {...sharedProps} suggestion={suggestion} />);
            break;
    }
};
